<template>
  <div v-if="visible" class="h5-scanner">
    <!-- 摄像头画面由 JS 动态 append 到这里 -->
    <div ref="host" class="h5-scanner-host" />

    <!-- 遮罩层：中间镂空，框外半透明深绿 -->
    <div class="h5-scanner-mask">
      <div class="h5-scanner-viewport">
        <div class="corner tl" />
        <div class="corner tr" />
        <div class="corner bl" />
        <div class="corner br" />
        <div class="h5-scanner-line" />
      </div>
    </div>

    <!-- 底部区域：提示 + 取消 -->
    <div class="h5-scanner-bottom">
      <span class="h5-scanner-tip">将核销码对准框内，自动识别</span>
      <div class="h5-scanner-cancel" @click="emitClose">取消</div>
      <span v-if="errorMsg" class="h5-scanner-error">{{ errorMsg }}</span>
    </div>
  </div>
</template>

<script>
import jsQR from 'jsqr'

/**
 * H5 摄像头扫码组件（Vue2 版）
 * 使用本地 jsqr 依赖进行二维码解码
 */
export default {
  name: 'H5Scanner',
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      errorMsg: '',
      videoEl: null,
      canvasEl: null,
      stream: null,
      raf: null,
      decoding: false,
      stopped: false
    }
  },
  watch: {
    visible(v) {
      if (v) this.$nextTick(this.openCamera)
      else this.closeCamera()
    }
  },
  beforeDestroy() {
    this.closeCamera()
  },
  methods: {
    /** 打开摄像头 */
    async openCamera() {
      this.errorMsg = ''
      this.stopped = false
      const hostEl = this.$refs.host
      if (!hostEl) {
        this.errorMsg = '扫码容器初始化失败'
        return
      }

      // 原生 video
      const video = document.createElement('video')
      video.setAttribute('autoplay', '')
      video.setAttribute('playsinline', 'true')
      video.setAttribute('webkit-playsinline', 'true')
      video.setAttribute('x5-playsinline', 'true')
      video.setAttribute('muted', '')
      video.removeAttribute('controls')
      video.setAttribute('disablepictureinpicture', '')
      video.className = 'h5-scanner-video'
      hostEl.appendChild(video)
      this.videoEl = video

      const canvas = document.createElement('canvas')
      canvas.className = 'h5-scanner-canvas'
      hostEl.appendChild(canvas)
      this.canvasEl = canvas

      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          this.errorMsg = '当前环境不支持摄像头（需 HTTPS），请改用手动输入'
          return
        }
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false
        })
        video.srcObject = this.stream
        await video.play().catch(() => {})
        this.decodeLoop()
      } catch (e) {
        const name = (e && e.name) || ''
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          this.errorMsg = '已拒绝摄像头权限，请在浏览器设置中允许'
        } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          this.errorMsg = '未找到可用摄像头'
        } else if (name === 'NotReadableError' || name === 'TrackStartError') {
          this.errorMsg = '摄像头被占用，请关闭其他应用'
        } else {
          this.errorMsg = '摄像头调用失败（' + (name || 'UNKNOWN') + '）'
        }
      }
    },

    /** 解码循环 */
    decodeLoop() {
      if (this.stopped) return
      const canvas = this.canvasEl
      const video = this.videoEl
      if (!canvas || !video || video.readyState !== video.HAVE_ENOUGH_DATA) {
        this.raf = requestAnimationFrame(this.decodeLoop)
        return
      }
      const vw = video.videoWidth
      const vh = video.videoHeight
      if (!vw || !vh) {
        this.raf = requestAnimationFrame(this.decodeLoop)
        return
      }
      // 裁剪中间偏上正方形区域
      const size = Math.floor(Math.min(vw, vh) * 0.5)
      const sx = Math.floor((vw - size) / 2)
      const sy = Math.floor((vh - size) / 2)
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      if (!ctx) {
        this.raf = requestAnimationFrame(this.decodeLoop)
        return
      }
      ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size)

      if (!this.decoding) {
        this.decoding = true
        try {
          const img = ctx.getImageData(0, 0, size, size)
          const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' })
          if (code && code.data) {
            this.$emit('success', code.data)
            this.decoding = false
            return
          }
        } catch (e) { /* skip */ }
        this.decoding = false
      }
      this.raf = requestAnimationFrame(this.decodeLoop)
    },

    /** 关闭摄像头 */
    closeCamera() {
      this.stopped = true
      if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null }
      if (this.stream) { this.stream.getTracks().forEach(t => t.stop()); this.stream = null }
      if (this.videoEl) { this.videoEl.srcObject = null; this.videoEl.remove(); this.videoEl = null }
      if (this.canvasEl) { this.canvasEl.remove(); this.canvasEl = null }
    },

    emitClose() {
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss">
/* 非 scoped：动态 video/canvas 需要全局样式 */
.h5-scanner {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 99999;
  background: #000;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.h5-scanner-host {
  position: absolute;
  inset: 0;
}
.h5-scanner-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}
.h5-scanner-video::-webkit-media-controls,
.h5-scanner-video::-webkit-media-controls-panel,
.h5-scanner-video::-webkit-media-controls-play-button,
.h5-scanner-video::-webkit-media-controls-start-playback-button,
.h5-scanner-video::-webkit-media-controls-enclosure,
.h5-scanner-video::-webkit-media-overlay-play-button {
  display: none !important;
  -webkit-appearance: none;
}
.h5-scanner-canvas {
  position: fixed;
  left: -9999px;
  top: -9999px;
  width: 200px;
  height: 200px;
}
.h5-scanner-mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 53.333vw; /* 200px @375稿 */
  z-index: 2;
}
.h5-scanner-viewport {
  position: relative;
  width: 80vw;   /* 300px @375稿 */
  height: 80vw;
  border-radius: 2.133vw; /* 8px */
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.85);
}
.h5-scanner-viewport .corner {
  position: absolute;
  width: 9.6vw;   /* 36px @375稿 */
  height: 9.6vw;
  border: 1.067vw solid #c9a36a; /* 4px */
  border-radius: 0.8vw; /* 3px */
}
.h5-scanner-viewport .corner.tl { top: -0.533vw; left: -0.533vw; border-right: none; border-bottom: none; }
.h5-scanner-viewport .corner.tr { top: -0.533vw; right: -0.533vw; border-left: none; border-bottom: none; }
.h5-scanner-viewport .corner.bl { bottom: -0.533vw; left: -0.533vw; border-right: none; border-top: none; }
.h5-scanner-viewport .corner.br { bottom: -0.533vw; right: -0.533vw; border-left: none; border-top: none; }
.h5-scanner-line {
  position: absolute;
  left: 5%;
  right: 5%;
  height: 1.067vw; /* 4px @375稿 */
  border-radius: 0.533vw;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(201, 163, 106, 0.25) 15%,
    #c9a36a 50%,
    rgba(201, 163, 106, 0.25) 85%,
    transparent 100%);
  box-shadow: 0 0 2.133vw 0.267vw rgba(201, 163, 106, 0.85);
  animation: h5scanLine 2.4s ease-in-out infinite;
}
@keyframes h5scanLine {
  0%   { top: 5%; }
  50%  { top: 93%; }
  100% { top: 5%; }
}
.h5-scanner-bottom {
  position: absolute;
  bottom: calc(16vw + env(safe-area-inset-bottom)); /* 60px @375稿 */
  left: 0;
  right: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6.4vw; /* 24px @375稿 */
}
.h5-scanner-tip {
  font-size: 4.8vw; /* 18px @375稿，原16px放大 */
  color: #f5e6c8;
  letter-spacing: 0.533vw;
  text-shadow: 0 0.267vw 1.333vw rgba(0, 0, 0, 0.7);
}
.h5-scanner-cancel {
  min-width: 53.333vw; /* 200px @375稿 */
  padding: 3.733vw 0;  /* 14px */
  border-radius: 7.467vw; /* 28px */
  background: linear-gradient(135deg, #1a1a1a 0%, #000 100%);
  border: 0.267vw solid rgba(212, 175, 55, 0.85);
  color: #f5e6c8;
  font-size: 5.067vw; /* 19px @375稿，原17px放大 */
  text-align: center;
  letter-spacing: 0.8vw;
  box-shadow: 0 0.533vw 2.667vw rgba(0, 0, 0, 0.3);
}
.h5-scanner-error {
  font-size: 4vw; /* 15px @375稿，原14px放大 */
  color: #ff9a9a;
  text-align: center;
  max-width: 80vw; /* 300px @375稿 */
  line-height: 1.5;
  text-shadow: 0 0.267vw 1.067vw rgba(0, 0, 0, 0.6);
}
</style>
